/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { projectsUpdate } from "../funcs/projectsUpdate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { UpdateProjectResult } from "../models/components/updateprojectresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateProjectRequest,
  UpdateProjectSecurity,
} from "../models/operations/updateproject.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateProjectMutationVariables = {
  request: UpdateProjectRequest;
  security?: UpdateProjectSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateProjectMutationData = UpdateProjectResult;

export type UpdateProjectMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateProject projects
 *
 * @remarks
 * Update the display name of the current project.
 */
export function useUpdateProjectMutation(
  options?: MutationHookOptions<
    UpdateProjectMutationData,
    UpdateProjectMutationError,
    UpdateProjectMutationVariables
  >,
): UseMutationResult<
  UpdateProjectMutationData,
  UpdateProjectMutationError,
  UpdateProjectMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateProjectMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateProject(): MutationKey {
  return ["@gram/client", "projects", "update"];
}

export function buildUpdateProjectMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateProjectMutationVariables,
  ) => Promise<UpdateProjectMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateProject(),
    mutationFn: function updateProjectMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateProjectMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(projectsUpdate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
